import React, { useState, useEffect } from 'react';
import { Plus, Edit, X, Trash2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaHorarios() {
    const [horarios, setHorarios] = useState([]);
    const [gestiones, setGestiones] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [niveles, setNiveles] = useState([]);
    const [asignaturas, setAsignaturas] = useState([]);
    const [docentes, setDocentes] = useState([]);
    const [salas, setSalas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedGestion, setSelectedGestion] = useState(new Date().getFullYear());
    const [selectedDay, setSelectedDay] = useState('Todo');
    const [errors, setErrors] = useState({});

    const initialFormData = {
        gestion: new Date().getFullYear(),
        carrera: '',
        mencion: '',
        nivel: '',
        codigo: '',
        id_asignatura: '',
        prerequisito: '',
        id_docente: '',
        color_docente: '#3B82F6',
        paralelo: '',
        capacidad: '',
        dias: { L: false, M: false, MI: false, J: false, V: false },
        horarios_detalle: {}
    };

    const [formData, setFormData] = useState(initialFormData);

    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const diasCortos = { L: 'Lunes', M: 'Martes', MI: 'Miércoles', J: 'Jueves', V: 'Viernes' };

    // Generar slots de 30 minutos desde 07:00 hasta 22:00
    const generarHoras = () => {
        const slots = [];
        for (let h = 6; h <= 18; h++) {
            slots.push(`${h.toString().padStart(2, '0')}:00`);
            slots.push(`${h.toString().padStart(2, '0')}:30`);
        }
        return slots;
    };
    const horas = generarHoras();

    // Función para obtener el rango del slot (ej: "07:00 - 07:30")
    const getSlotRango = (horaInicio) => {
        const [h, m] = horaInicio.split(':').map(Number);
        let finH = h;
        let finM = m + 30;
        if (finM >= 60) {
            finM = 0;
            finH += 1;
        }
        const horaFin = `${finH.toString().padStart(2, '0')}:${finM.toString().padStart(2, '0')}`;
        return `${horaInicio} - ${horaFin}`;
    };

    const coloresHorario = ['bg-yellow-200', 'bg-orange-200', 'bg-purple-200', 'bg-blue-200', 'bg-green-200', 'bg-pink-200'];

    useEffect(() => {
        loadData();
    }, [selectedGestion]);

    const handleDownloadExcel = () => {
        try {
            if (!horarios || horarios.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            const data = horarios.map((h, idx) => {
                const detalles = h.horarios_detalle && Array.isArray(h.horarios_detalle) ? h.horarios_detalle : null;
                const cap = parseInt(h.capacidad) || 30;
                const ins = parseInt(h.inscritos) || 0;
                const porc = Math.max(0, Math.round(((cap - ins) / cap) * 100));

                return {
                    'No': idx + 1,
                    'Gestión': h.gestion || '-',
                    'Carrera': h.carrera || '-',
                    'Mención': h.mencion || '-',
                    'Año': h.nivel || '-',
                    'Código': h.codigo || getAsignaturaCodigo(h.id_asignatura),
                    'Asignatura': h.asignatura || getAsignaturaNombre(h.id_asignatura),
                    'Prerrequisito': h.prerequisito ? getAsignaturaNombre(h.prerequisito) : '-',
                    'Docente': h.docente_nombre ? `${h.docente_nombre} ${h.docente_apellido || ''}` : getDocenteNombre(h.id_docente),
                    'Sala': detalles ? detalles.map(d => d.sala || '-').join(', ') : (h.sala || h.aula || '-'),
                    'Paralelo': h.paralelo || '-',
                    'Capacidad': cap,
                    '% Capacidad': `${porc}%`,
                    'Hora Inicio': detalles ? detalles.map(d => d.hora_inicio?.substring(0, 5) || '-').join(', ') : (h.hora_inicio || '-'),
                    'Hora Fin': detalles ? detalles.map(d => d.hora_fin?.substring(0, 5) || '-').join(', ') : (h.hora_fin || '-'),
                    'Días': detalles ? detalles.map(d => getDiasAbrev(d.dia)).join(', ') : getDiasAbrev(h.dia_semana),
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Horarios');
            XLSX.writeFile(wb, `Horarios_Carrera_${selectedGestion}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [horariosRes, asignaturasRes, docentesRes, carrerasRes, mencionesRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/horarios?gestion=${selectedGestion}`),
                fetch(`${API_URL}/jefatura/asignaturas`),
                fetch(`${API_URL}/jefatura/docentes`),
                fetch(`${API_URL}/jefatura/carreras`),
                fetch(`${API_URL}/jefatura/menciones`)
            ]);

            const horariosData = await horariosRes.json();
            const asignaturasData = await asignaturasRes.json();
            const docentesData = await docentesRes.json();
            const carrerasData = await carrerasRes.json();
            const mencionesData = await mencionesRes.json();

            setHorarios(horariosData.horarios || []);
            setAsignaturas(asignaturasData.asignaturas || []);
            setDocentes(docentesData.docentes || []);
            setCarreras(carrerasData.carreras || []);
            setMenciones(mencionesData.menciones || []);

            // Gestiones: desde 2020 hasta año actual + 1
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let y = 2020; y <= currentYear + 1; y++) {
                years.push(y);
            }
            setGestiones(years);

            // Datos de referencia estáticos
            setNiveles(['1', '2', '3', '4']);
            // Salas del 1 al 20
            const salasArr = [];
            for (let i = 1; i <= 20; i++) {
                salasArr.push(i.toString());
            }
            setSalas(salasArr);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filtrar menciones por carrera seleccionada
    const getMencionesFiltradas = () => {
        if (!formData.carrera) return [];
        const carreraObj = carreras.find(c => c.nombre === formData.carrera);
        if (!carreraObj) return [];
        return menciones.filter(m => m.id_carrera === carreraObj.id_carrera);
    };

    // Filtrar asignaturas por carrera, mencion y nivel
    const getAsignaturasFiltradas = () => {
        if (!formData.carrera || !formData.mencion || !formData.nivel) return [];
        const carreraObj = carreras.find(c => c.nombre === formData.carrera);
        const mencionObj = menciones.find(m => m.nombre === formData.mencion && m.id_carrera === carreraObj?.id_carrera);
        if (!carreraObj || !mencionObj) return [];
        return asignaturas.filter(a =>
            a.id_carrera === carreraObj.id_carrera &&
            a.id_mencion === mencionObj.id_mencion &&
            String(a.nivel) === String(formData.nivel)
        );
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('dia_')) {
            const dia = name.replace('dia_', '');
            const diaFull = diasCortos[dia];
            setFormData(prev => {
                const newDias = { ...prev.dias, [dia]: checked };
                const newDetalle = { ...prev.horarios_detalle };
                if (checked) {
                    newDetalle[diaFull] = newDetalle[diaFull] || { hora_inicio: '', hora_fin: '', sala: '' };
                } else {
                    delete newDetalle[diaFull];
                }
                return { ...prev, dias: newDias, horarios_detalle: newDetalle };
            });
        } else if (name === 'carrera') {
            // Al cambiar carrera, resetear mencion, nivel, asignatura y código
            setFormData(prev => ({
                ...prev,
                carrera: value,
                mencion: '',
                nivel: '',
                id_asignatura: '',
                codigo: '',
                prerequisito: ''
            }));
        } else if (name === 'mencion') {
            // Al cambiar mencion, resetear nivel, asignatura y código
            setFormData(prev => ({
                ...prev,
                mencion: value,
                nivel: '',
                id_asignatura: '',
                codigo: '',
                prerequisito: ''
            }));
        } else if (name === 'nivel') {
            // Al cambiar nivel, resetear asignatura y código
            setFormData(prev => ({
                ...prev,
                nivel: value,
                id_asignatura: '',
                codigo: '',
                prerequisito: ''
            }));
        } else if (name === 'id_asignatura') {
            // Al seleccionar asignatura, auto-rellenar código y prerequisito
            const asig = asignaturas.find(a => String(a.id_asignatura) === String(value));
            setFormData(prev => ({
                ...prev,
                id_asignatura: value,
                codigo: asig?.codigo || '',
                prerequisito: asig?.prerequisito_codigo || ''
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleDetalleChange = (diaFull, field, value) => {
        setFormData(prev => ({
            ...prev,
            horarios_detalle: {
                ...prev.horarios_detalle,
                [diaFull]: {
                    ...prev.horarios_detalle[diaFull],
                    [field]: value
                }
            }
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.gestion) newErrors.gestion = 'Requerido';
        if (!formData.id_asignatura) newErrors.id_asignatura = 'Requerido';
        if (!formData.id_docente) newErrors.id_docente = 'Requerido';
        const diasSeleccionados = Object.values(formData.dias).some(v => v);
        if (!diasSeleccionados) newErrors.dias = 'Seleccione al menos un día';
        // Validar que cada día seleccionado tenga hora_inicio, hora_fin y sala
        if (diasSeleccionados) {
            Object.entries(formData.dias).forEach(([key, active]) => {
                if (active) {
                    const diaFull = diasCortos[key];
                    const detalle = formData.horarios_detalle[diaFull];
                    if (!detalle?.hora_inicio) newErrors[`detalle_${diaFull}_hora_inicio`] = 'Requerido';
                    if (!detalle?.hora_fin) newErrors[`detalle_${diaFull}_hora_fin`] = 'Requerido';
                    if (!detalle?.sala) newErrors[`detalle_${diaFull}_sala`] = 'Requerido';
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            const diasActivos = Object.entries(formData.dias).filter(([_, v]) => v).map(([d]) => diasCortos[d]);

            if (diasActivos.length === 0) {
                alert('Debe seleccionar al menos un día');
                return;
            }

            // Guardar múltiples días en un solo registro (separados por coma)
            const diasString = diasActivos.join(',');

            // Construir horarios_detalle desde el formulario
            const horariosDetalle = Object.entries(formData.horarios_detalle)
                .filter(([dia]) => diasActivos.includes(dia))
                .map(([dia, detalle]) => ({
                    dia,
                    hora_inicio: detalle.hora_inicio,
                    hora_fin: detalle.hora_fin,
                    sala: detalle.sala
                }));

            const payload = {
                gestion: parseInt(formData.gestion),
                carrera: formData.carrera,
                mencion: formData.mencion,
                nivel: formData.nivel,
                codigo: formData.codigo,
                id_asignatura: formData.id_asignatura,
                prerequisito: formData.prerequisito || null,
                id_docente: formData.id_docente,
                color_docente: formData.color_docente || '#3B82F6',
                sala: horariosDetalle[0]?.sala || '',
                paralelo: formData.paralelo,
                capacidad: formData.capacidad || 30,
                hora_inicio: horariosDetalle[0]?.hora_inicio || '',
                hora_fin: horariosDetalle[0]?.hora_fin || '',
                dia_semana: diasString,
                horarios_detalle: horariosDetalle
            };

            const url = editingId
                ? `${API_URL}/jefatura/horarios/${editingId}`
                : `${API_URL}/jefatura/horarios`;

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const error = await response.json();
                alert(error.error || 'Error al guardar');
                return;
            }

            setShowModal(false);
            setEditingId(null);
            setFormData(initialFormData);
            loadData();
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar');
        }
    };

    const handleEdit = (horario) => {
        setEditingId(horario.id_horario);

        // Parsear múltiples días desde dia_semana (puede ser "Lunes,Martes,Jueves")
        const diasGuardados = horario.dia_semana ? horario.dia_semana.split(',') : [];
        const diasObj = { L: false, M: false, MI: false, J: false, V: false };
        diasGuardados.forEach(dia => {
            const key = Object.entries(diasCortos).find(([k, v]) => v === dia.trim())?.[0];
            if (key) diasObj[key] = true;
        });

        const horaInicio = horario.hora_inicio ? horario.hora_inicio.substring(0, 5) : '';
        const horaFin = horario.hora_fin ? horario.hora_fin.substring(0, 5) : '';

        // Cargar horarios_detalle si existe, sino migrar desde campos legacy
        let horariosDetalle = {};
        if (horario.horarios_detalle && Array.isArray(horario.horarios_detalle) && horario.horarios_detalle.length > 0) {
            horario.horarios_detalle.forEach(d => {
                horariosDetalle[d.dia] = {
                    hora_inicio: d.hora_inicio || '',
                    hora_fin: d.hora_fin || '',
                    sala: d.sala || ''
                };
            });
        } else {
            // Migrar: asignar misma hora/sala a todos los días seleccionados
            diasGuardados.forEach(dia => {
                horariosDetalle[dia.trim()] = {
                    hora_inicio: horaInicio,
                    hora_fin: horaFin,
                    sala: horario.sala || horario.aula || ''
                };
            });
        }

        setFormData({
            gestion: horario.gestion || new Date().getFullYear(),
            carrera: horario.carrera || '',
            mencion: horario.mencion || '',
            nivel: horario.nivel || '',
            codigo: horario.codigo || '',
            id_asignatura: horario.id_asignatura?.toString() || '',
            prerequisito: horario.prerequisito?.toString() || '',
            id_docente: horario.id_docente?.toString() || '',
            color_docente: horario.color_docente || '#3B82F6',
            paralelo: horario.paralelo || '',
            capacidad: horario.capacidad || 30,
            dias: diasObj,
            horarios_detalle: horariosDetalle
        });
        setErrors({});
        setShowModal(true);
    };

    const handleNew = () => {
        setEditingId(null);
        setFormData({ ...initialFormData, gestion: selectedGestion });
        setErrors({});
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar esta asignatura?')) return;
        try {
            const response = await fetch(`${API_URL}/jefatura/horarios/${id}`, { method: 'DELETE' });
            if (response.ok) {
                loadData();
            } else {
                const error = await response.json();
                alert(error.error || 'Error al eliminar');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar');
        }
    };

    const getDiasAbrev = (diasStr) => {
        if (!diasStr) return '-';
        const map = { 'Lunes': 'L', 'Martes': 'M', 'Miércoles': 'MI', 'Jueves': 'J', 'Viernes': 'V' };
        return diasStr.split(',').map(d => map[d.trim()] || d.trim()).join(', ');
    };

    const getColorByIndex = (index) => coloresHorario[index % coloresHorario.length];

    const getHorariosForCell = (dia, slotHora) => {
        // Convertir string "HH:MM" a minutos para comparar
        const horaToMinutos = (horaStr) => {
            if (!horaStr) return 0;
            const [h, m] = horaStr.substring(0, 5).split(':').map(Number);
            return h * 60 + m;
        };

        const slotMinutos = horaToMinutos(slotHora);

        return horarios.filter(h => {
            // Verificar que el día coincida
            if (!h.dia_semana || !h.dia_semana.includes(dia)) return false;

            // Si tiene horarios_detalle, usar el horario específico del día
            if (h.horarios_detalle && Array.isArray(h.horarios_detalle)) {
                const detalleDia = h.horarios_detalle.find(d => d.dia === dia);
                if (detalleDia) {
                    const inicioMinutos = horaToMinutos(detalleDia.hora_inicio);
                    const finMinutos = horaToMinutos(detalleDia.hora_fin);
                    return slotMinutos >= inicioMinutos && slotMinutos < finMinutos;
                }
                return false;
            }

            // Fallback: usar hora_inicio/hora_fin globales
            const inicioMinutos = horaToMinutos(h.hora_inicio);
            const finMinutos = horaToMinutos(h.hora_fin);
            return slotMinutos >= inicioMinutos && slotMinutos < finMinutos;
        });
    };

    const getDocenteNombre = (idDocente) => {
        const docente = docentes.find(d => d.id_docente == idDocente);
        return docente ? `${docente.nombres || ''} ${docente.apellidos || ''}`.trim() : '-';
    };

    const getAsignaturaNombre = (idAsignatura) => {
        const asig = asignaturas.find(a => a.id_asignatura == idAsignatura);
        return asig?.nombre || '-';
    };

    const getAsignaturaCodigo = (idAsignatura) => {
        const asig = asignaturas.find(a => a.id_asignatura == idAsignatura);
        return asig?.codigo || '-';
    };

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Asignaturas</h1>
            </div>

            {/* Toolbar */}
            <div className="mb-4 flex flex-wrap gap-3 items-center">
                <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                    <Plus size={20} />
                    <span>Adicionar Asignatura</span>
                </button>

                <div className="relative">
                    <select
                        value={selectedGestion}
                        onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                        className="px-6 py-2 pr-10 border border-gray-300 rounded-full bg-white text-sm appearance-none"
                    >
                        {gestiones.map(g => (
                            <option key={g} value={g}>Año {g}</option>
                        ))}
                    </select>
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">▼</span>
                </div>

                <button
                    onClick={() => setShowCalendarModal(true)}
                    className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                >
                    Ver Horario
                </button>

                <button
                    onClick={handleDownloadExcel}
                    className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                    title="Descargar Excel"
                >
                    <Download size={20} />
                </button>
            </div>

            {/* Tabla de Horarios */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-3 text-center font-semibold border">No</th>
                                <th className="px-2 py-3 text-center font-semibold border">Gestion</th>
                                <th className="px-2 py-3 text-center font-semibold border">Carrera</th>
                                <th className="px-2 py-3 text-center font-semibold border">Mencion</th>
                                <th className="px-2 py-3 text-center font-semibold border">Año</th>
                                <th className="px-2 py-3 text-center font-semibold border">Codigo</th>
                                <th className="px-2 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-2 py-3 text-center font-semibold border">Prerequisito</th>
                                <th className="px-2 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-2 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-2 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-2 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-2 py-3 text-center font-semibold border">% Capacidad</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Inicio</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Fin</th>
                                <th className="px-2 py-3 text-center font-semibold border">Dias</th>
                                <th className="px-2 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {horarios.length === 0 ? (
                                <tr><td colSpan="16" className="px-4 py-8 text-center text-gray-500 border">No hay horarios registrados</td></tr>
                            ) : (
                                horarios.map((h, index) => {
                                    // Preparar info de horarios por día
                                    const detalles = h.horarios_detalle && Array.isArray(h.horarios_detalle) ? h.horarios_detalle : null;
                                    return (
                                        <tr key={h.id_horario} className="hover:bg-gray-50">
                                            <td className="px-2 py-2 text-center border">{index + 1}</td>
                                            <td className="px-2 py-2 text-center border">{h.gestion || '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.carrera || '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.mencion || '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.nivel || '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.codigo || getAsignaturaCodigo(h.id_asignatura)}</td>
                                            <td className="px-2 py-2 text-center border">{h.asignatura || getAsignaturaNombre(h.id_asignatura)}</td>
                                            <td className="px-2 py-2 text-center border">{h.prerequisito ? getAsignaturaNombre(h.prerequisito) : '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.docente_nombre ? `${h.docente_nombre} ${h.docente_apellido || ''}` : getDocenteNombre(h.id_docente)}</td>
                                            <td className="px-2 py-2 text-center border">
                                                {detalles ? detalles.map((d, i) => <div key={i}>{d.sala || '-'}</div>) : (h.sala || h.aula || '-')}
                                            </td>
                                            <td className="px-2 py-2 text-center border">{h.paralelo || '-'}</td>
                                            <td className="px-2 py-2 text-center border">{h.capacidad || 30}</td>
                                            <td className="px-2 py-2 text-center border">
                                                {(() => {
                                                    const cap = parseInt(h.capacidad) || 30;
                                                    const ins = parseInt(h.inscritos) || 0;
                                                    const porc = Math.max(0, Math.round(((cap - ins) / cap) * 100));
                                                    return (
                                                        <span className={`font-bold ${porc < 20 ? 'text-red-600' : porc < 50 ? 'text-orange-600' : 'text-green-600'}`}>
                                                            {porc}%
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-2 py-2 text-center border">
                                                {detalles ? detalles.map((d, i) => <div key={i}>{d.hora_inicio?.substring(0, 5) || '-'}</div>) : (h.hora_inicio || '-')}
                                            </td>
                                            <td className="px-2 py-2 text-center border">
                                                {detalles ? detalles.map((d, i) => <div key={i}>{d.hora_fin?.substring(0, 5) || '-'}</div>) : (h.hora_fin || '-')}
                                            </td>
                                            <td className="px-2 py-2 text-center border">
                                                {detalles ? detalles.map((d, i) => <div key={i}>{getDiasAbrev(d.dia)}</div>) : getDiasAbrev(h.dia_semana)}
                                            </td>
                                            <td className="px-2 py-2 text-center border">
                                                <div className="flex gap-1 justify-center">
                                                    <button onClick={() => handleEdit(h)} className="p-1.5 bg-blue-900 text-white rounded-full hover:bg-blue-800" title="Editar">
                                                        <Edit size={14} />
                                                    </button>
                                                    <button onClick={() => handleDelete(h.id_horario)} className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700" title="Eliminar">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Añadir Asignatura */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Añadir Asignatura</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <X size={28} />
                            </button>
                        </div>
                        <div className="p-6">
                            {/* Fila 1: Gestion, Carrera, Mencion, Año, Codigo */}
                            <div className="grid grid-cols-5 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Gestion</label>
                                    <select name="gestion" value={formData.gestion} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none">
                                        <option value="">Seleccionar...</option>
                                        {gestiones.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Carrera</label>
                                    <select name="carrera" value={formData.carrera} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none">
                                        <option value="">Seleccionar...</option>
                                        {carreras.map(c => <option key={c.id_carrera} value={c.nombre}>{c.nombre}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Mencion</label>
                                    <select name="mencion" value={formData.mencion} onChange={handleInputChange}
                                        disabled={!formData.carrera}
                                        className={`w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none ${!formData.carrera ? 'bg-gray-100' : ''}`}>
                                        <option value="">{formData.carrera ? 'Seleccionar...' : 'Primero seleccione carrera'}</option>
                                        {getMencionesFiltradas().map(m => <option key={m.id_mencion} value={m.nombre}>{m.nombre}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Año</label>
                                    <select name="nivel" value={formData.nivel} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none">
                                        <option value="">Seleccionar...</option>
                                        {niveles.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Codigo</label>
                                    <input type="text" name="codigo" value={formData.codigo} readOnly
                                        placeholder="Se autocompletara"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md bg-gray-100" />
                                </div>
                            </div>

                            {/* Fila 2: Asignatura, Prerequisito, Docente, Color */}
                            <div className="grid grid-cols-4 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Asignatura</label>
                                    <select name="id_asignatura" value={formData.id_asignatura} onChange={handleInputChange}
                                        disabled={!formData.carrera || !formData.mencion || !formData.nivel}
                                        className={`w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none ${(!formData.carrera || !formData.mencion || !formData.nivel) ? 'bg-gray-100' : ''}`}>
                                        <option value="">{(!formData.carrera || !formData.mencion || !formData.nivel) ? 'Seleccione carrera, mención y año' : 'Seleccionar...'}</option>
                                        {getAsignaturasFiltradas().map(a => <option key={a.id_asignatura} value={a.id_asignatura}>{a.nombre}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Prerequisito</label>
                                    <input type="text" name="prerequisito" value={formData.prerequisito} readOnly
                                        placeholder="Se autocompletara"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md bg-gray-100" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Docente</label>
                                    <select name="id_docente" value={formData.id_docente} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md appearance-none">
                                        <option value="">Seleccionar...</option>
                                        {docentes.map(d => <option key={d.id_docente} value={d.id_docente}>{d.nombres} {d.apellidos}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Color Horario</label>
                                    <input type="color" name="color_docente" value={formData.color_docente} onChange={handleInputChange}
                                        className="w-full h-10 px-1 py-1 border-2 border-gray-300 rounded-md cursor-pointer" />
                                </div>
                            </div>

                            {/* Fila 3: Paralelo, Capacidad */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Paralelo</label>
                                    <input
                                        type="text"
                                        name="paralelo"
                                        value={formData.paralelo}
                                        onChange={(e) => {
                                            const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                                            setFormData(prev => ({ ...prev, paralelo: val }));
                                        }}
                                        placeholder="Ej: A1"
                                        maxLength={2}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Capacidad</label>
                                    <input type="number" name="capacidad" value={formData.capacidad} onChange={handleInputChange}
                                        placeholder="Ej: 30"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md" />
                                </div>
                            </div>

                            {/* Días con horarios por día */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Días y Horarios por Día</label>
                                <div className="space-y-3">
                                    {Object.entries(diasCortos).map(([key, diaFull]) => {
                                        const isActive = formData.dias[key];
                                        const detalle = formData.horarios_detalle[diaFull] || {};
                                        return (
                                            <div key={key} className={`border rounded-lg p-3 ${isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name={`dia_${key}`}
                                                        checked={isActive}
                                                        onChange={handleInputChange}
                                                        className="w-5 h-5 border-2 border-gray-300 rounded text-blue-600"
                                                    />
                                                    <span className="font-medium text-sm">{diaFull}</span>
                                                </label>
                                                {isActive && (
                                                    <div className="grid grid-cols-3 gap-3 mt-3 pl-8">
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Hora Inicio</label>
                                                            <input
                                                                type="time"
                                                                value={detalle.hora_inicio || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'hora_inicio', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Hora Fin</label>
                                                            <input
                                                                type="time"
                                                                value={detalle.hora_fin || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'hora_fin', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Sala</label>
                                                            <select
                                                                value={detalle.sala || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'sala', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm appearance-none"
                                                            >
                                                                <option value="">Sala...</option>
                                                                {detalle.sala && !salas.includes(detalle.sala) && (
                                                                    <option value={detalle.sala}>{detalle.sala}</option>
                                                                )}
                                                                {salas.map(s => <option key={s} value={s}>{s}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {errors.dias && <p className="text-red-500 text-xs mt-1">{errors.dias}</p>}
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4">
                            <button onClick={handleSubmit} className="px-8 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 font-medium">
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver Horario (Calendario) */}
            {showCalendarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-lg font-semibold">Horario de Asignaturas</h2>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        try {
                                            if (!horarios || horarios.length === 0) {
                                                alert('No hay datos para exportar');
                                                return;
                                            }

                                            const data = horarios.map((h, idx) => {
                                                const detalles = h.horarios_detalle && Array.isArray(h.horarios_detalle) ? h.horarios_detalle : null;
                                                return {
                                                    'No': idx + 1,
                                                    'Carrera': h.carrera || '-',
                                                    'Asignatura': h.asignatura || getAsignaturaNombre(h.id_asignatura),
                                                    'Docente': h.docente_nombre ? `${h.docente_nombre} ${h.docente_apellido || ''}` : getDocenteNombre(h.id_docente),
                                                    'Sala': detalles ? detalles.map(d => d.sala || '-').join(', ') : (h.sala || '-'),
                                                    'Paralelo': h.paralelo || '-',
                                                    'Hora Inicio': detalles ? detalles.map(d => d.hora_inicio?.substring(0, 5) || '-').join(', ') : (h.hora_inicio || '-'),
                                                    'Hora Fin': detalles ? detalles.map(d => d.hora_fin?.substring(0, 5) || '-').join(', ') : (h.hora_fin || '-'),
                                                    'Días': detalles ? detalles.map(d => getDiasAbrev(d.dia)).join(', ') : getDiasAbrev(h.dia_semana),
                                                };
                                            });

                                            const ws = XLSX.utils.json_to_sheet(data);
                                            const wb = XLSX.utils.book_new();
                                            XLSX.utils.book_append_sheet(wb, ws, 'Horario');
                                            XLSX.writeFile(wb, `Horario_General_${selectedGestion}.xlsx`);
                                        } catch (error) {
                                            console.error('Error al exportar Horario:', error);
                                            alert('Error al generar el archivo Excel.');
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-1.5 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium transition"
                                >
                                    <Download size={16} />
                                    <span>Descargar</span>
                                </button>
                                <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={28} />
                                </button>
                            </div>
                        </div>

                        {/* Botones de días */}
                        <div className="px-6 py-4 flex gap-2">
                            <button onClick={() => setSelectedDay('Lunes')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Lunes' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>L</button>
                            <button onClick={() => setSelectedDay('Martes')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Martes' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>M</button>
                            <button onClick={() => setSelectedDay('Miércoles')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Miércoles' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>MI</button>
                            <button onClick={() => setSelectedDay('Jueves')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Jueves' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>J</button>
                            <button onClick={() => setSelectedDay('Viernes')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Viernes' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>V</button>
                            <button onClick={() => setSelectedDay('Todo')} className={`px-4 py-1 rounded-full text-sm font-medium ${selectedDay === 'Todo' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>Todo</button>
                        </div>

                        {/* Calendario */}
                        <div className="px-6 pb-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr>
                                        <th className="border p-2 bg-gray-100 w-20">Horario</th>
                                        {(selectedDay === 'Todo' ? diasSemana : [selectedDay]).map(dia => (
                                            <th key={dia} className="border p-2 bg-gray-100">{dia}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {horas.map(hora => (
                                        <tr key={hora}>
                                            <td className="border px-1 py-0.5 text-center text-xs font-medium bg-gray-50 whitespace-nowrap">{getSlotRango(hora)}</td>
                                            {(selectedDay === 'Todo' ? diasSemana : [selectedDay]).map(dia => {
                                                const horariosEnCelda = getHorariosForCell(dia, hora);
                                                return (
                                                    <td key={`${dia}-${hora}`} className="border p-0 min-w-[100px] h-8 align-middle">
                                                        {horariosEnCelda.map((h, i) => (
                                                            <div
                                                                key={i}
                                                                className="text-xs px-1 h-full w-full flex flex-col justify-center rounded-none shadow-inner truncate"
                                                                style={{ backgroundColor: h.color_docente || '#3B82F6', color: '#fff' }}
                                                                title={`${h.asignatura || getAsignaturaNombre(h.id_asignatura)} - ${getDocenteNombre(h.id_docente)} - Sala ${h.sala} - Par. ${h.paralelo}`}
                                                            >
                                                                <span className="font-bold">{h.asignatura || getAsignaturaNombre(h.id_asignatura)}</span>
                                                            </div>
                                                        ))}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end px-6 py-4 border-t">
                            <button onClick={() => setShowCalendarModal(false)} className="px-8 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                                Atras
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
